import { useState, useEffect, useRef } from "react";
import api, { downloadFile, ORIGIN } from "@/lib/api";
import { allDocuments } from "@/lib/tree";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  FileText,
  FileArchive,
  FileType,
  Globe,
  FileCode,
  BookMarked,
  BookOpen,
  Loader2,
  Link2,
  Copy,
  Check,
  Layers,
  Download,
  History,
  RotateCw,
  X,
  Bookmark,
  Plus,
  Trash2,
  Play,
  FolderTree,
  ChevronDown,
  ChevronUp,
  Image as ImageIcon,
  Mail,
} from "lucide-react";

const FORMATS = [
  { fmt: "pdf", icon: BookMarked, name: "Master PDF", desc: "A bound publication with cover, table of contents and every document." },
  { fmt: "epub", icon: BookOpen, name: "EPUB", desc: "A reflowable e-book with chapters and navigation for any e-reader." },
  { fmt: "zip", icon: FileArchive, name: "Structured ZIP", desc: "Publication → module → document hierarchy as Markdown files." },
  { fmt: "pdf_structured", icon: FolderTree, name: "Structured PDFs (ZIP)", desc: "Every document as its own PDF, foldered by module & section." },
  { fmt: "md", icon: FileText, name: "Markdown", desc: "The whole publication as a single combined Markdown file." },
  { fmt: "docx", icon: FileType, name: "Word (DOCX)", desc: "Editable Microsoft Word document." },
  { fmt: "html", icon: Globe, name: "HTML", desc: "Styled, self-contained web page." },
  { fmt: "txt", icon: FileCode, name: "Plain text", desc: "Clean, formatting-free text export." },
];
const FORMAT_NAME = Object.fromEntries(FORMATS.map((f) => [f.fmt, f.name]));

const EXT = { pdf: "_master.pdf", epub: ".epub", zip: ".zip", md: ".md", docx: ".docx", html: ".html", txt: ".txt", pdf_structured: "_structured_pdfs.zip" };
const HEAVY = ["pdf", "docx", "epub", "pdf_structured"];
const ASYNC_THRESHOLD = 40; // heavy formats render in the background above this
const ANY_ASYNC_THRESHOLD = 800; // very large books render every format in the background
const SPLIT_NUDGE_THRESHOLD = 500; // suggest per-module Split PDFs at/above this many docs
const HERO_SPLIT_THRESHOLD = 500; // one-click PDF auto-builds faster per-module Split ZIP above this

function fmtSecs(s) {
  if (s == null || !isFinite(s) || s < 0) return "";
  if (s < 60) return `${Math.round(s)}s`;
  const m = Math.floor(s / 60);
  const r = Math.round(s % 60);
  return `${m}m${r ? ` ${r}s` : ""}`;
}

function fmtBytes(n) {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function ExportPanel({ projectId, project, tree }) {
  const [busy, setBusy] = useState(null);
  const [sharing, setSharing] = useState(null);
  const [links, setLinks] = useState({});
  const [copied, setCopied] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [presets, setPresets] = useState([]);
  const [presetOpen, setPresetOpen] = useState(false);
  const [presetName, setPresetName] = useState("");
  const [presetSel, setPresetSel] = useState({}); // fmt -> true
  const [presetSplit, setPresetSplit] = useState(false);
  const [savingPreset, setSavingPreset] = useState(false);
  const [runningPreset, setRunningPreset] = useState(null);
  const [sharingPreset, setSharingPreset] = useState(null);
  const [presetLinks, setPresetLinks] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [heroBusy, setHeroBusy] = useState(false);
  const [heroProg, setHeroProg] = useState(null); // {done,total,startedAt}
  const [showMore, setShowMore] = useState(false);
  const [formatProg, setFormatProg] = useState({}); // run-key -> {done,total,startedAt}
  const [emailWhenReady, setEmailWhenReady] = useState(false);
  const [cover, setCover] = useState(null); // {data_uri,...} or null
  const [coverBusy, setCoverBusy] = useState(false);
  const coverInputRef = useRef(null);
  const [dcSize, setDcSize] = useState(50);
  const [dcAutoCombine, setDcAutoCombine] = useState(false);
  const [dcBusy, setDcBusy] = useState(false);
  const [combineBusy, setCombineBusy] = useState(false);
  const [retrying, setRetrying] = useState({}); // {partIndex: true} — single-part rebuilds in flight
  const [dcJob, setDcJob] = useState(null); // {status, progress, chunks:[...]}
  const docs = allDocuments(tree);
  const bigBook = docs.length >= SPLIT_NUDGE_THRESHOLD;
  const heroSplit = docs.length >= HERO_SPLIT_THRESHOLD;
  const anyAsync = docs.length > ASYNC_THRESHOLD; // background builds are used at this size
  const emailParam = () => (emailWhenReady ? "&email=true" : "");
  const needsAsync = (fmt) =>
    (HEAVY.includes(fmt) && docs.length > ASYNC_THRESHOLD) || docs.length > ANY_ASYNC_THRESHOLD;

  const loadJobs = () =>
    api.get(`/projects/${projectId}/export-jobs`).then((r) => setJobs(r.data)).catch(() => {});

  const loadPresets = () =>
    api.get(`/export-presets`).then((r) => setPresets(r.data)).catch(() => {});

  const loadCover = () =>
    api.get(`/projects/${projectId}/cover`).then((r) => setCover(r.data.cover)).catch(() => {});

  const loadDownloadCenter = () =>
    api.get(`/projects/${projectId}/download-center/latest`)
      .then((r) => {
        if (!r.data.job_id) return;
        setDcJob(r.data);
        if (r.data.auto_combine) setDcAutoCombine(true);
        if (r.data.status === "building" || r.data.status === "queued") {
          setDcBusy(true);
          pollDownloadCenter(r.data.job_id).finally(() => setDcBusy(false));
        } else if (r.data.combined?.status === "building") {
          // a combine was still stitching when we left — resume watching it
          setCombineBusy(true);
          pollCombine(r.data.job_id).finally(() => setCombineBusy(false));
        }
      })
      .catch(() => {});

  useEffect(() => {
    loadJobs();
    loadPresets();
    loadCover();
    loadDownloadCenter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const pollDownloadCenter = async (jobId) => {
    const deadline = Date.now() + 60 * 60 * 1000;
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 2000));
      try {
        const { data } = await api.get(`/download-center/${jobId}`);
        setDcJob(data);
        if (data.status === "ready" || data.status === "error") return data;
      } catch {
        /* transient */
      }
    }
    return null;
  };

  const prepareParts = async () => {
    setDcBusy(true);
    setDcJob(null);
    try {
      const { data } = await api.post(
        `/projects/${projectId}/download-center?chunk_size=${dcSize}&auto_combine=${dcAutoCombine}&email=${emailWhenReady}`
      );
      toast.success("Building your download parts…");
      const res = await pollDownloadCenter(data.job_id);
      if (res && res.status === "ready") {
        const failed = (res.chunks || []).filter((c) => c.status === "error").length;
        if (failed) toast.warning(`${failed} part${failed === 1 ? "" : "s"} failed — retry just those, the rest are ready.`);
        else toast.success("All parts ready — download any you like");
        // Auto-combine: the backend starts the merge right after the last part; keep watching it.
        if (!failed && dcAutoCombine && res.all_parts_ready && res.combined?.status !== "ready") {
          setCombineBusy(true);
          const cres = await pollCombine(data.job_id).finally(() => setCombineBusy(false));
          if (cres?.combined?.status === "ready") toast.success("Complete PDF ready — one file for the whole book");
        }
      }
      if (res && res.status === "error") toast.error(res.error || "Some parts failed");
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Could not prepare parts");
    } finally {
      setDcBusy(false);
    }
  };

  // Rebuild ONE failed part (or all failed parts) without recreating the ready ones.
  // A single-part retry runs independently — it does NOT lock the whole panel, so you can
  // retry a failed part even while the rest of a huge book is still building.
  const retryParts = async (partIndex = null) => {
    if (!dcJob?.job_id) return;
    if (partIndex == null) {
      setDcBusy(true);
      try {
        await api.post(`/projects/${projectId}/download-center/${dcJob.job_id}/retry-part`);
        toast.info("Rebuilding failed parts…");
        const res = await pollDownloadCenter(dcJob.job_id);
        if (res && res.status === "ready") {
          const failed = (res.chunks || []).filter((c) => c.status === "error").length;
          if (failed) toast.warning(`${failed} part${failed === 1 ? "" : "s"} still failed — try again.`);
          else toast.success("Parts rebuilt — all ready");
        }
      } catch (err) {
        toast.error(err.response?.data?.detail || err.message || "Retry failed");
      } finally {
        setDcBusy(false);
      }
      return;
    }
    // Single-part retry — independent, keeps the rest of the UI usable.
    setRetrying((m) => ({ ...m, [partIndex]: true }));
    try {
      await api.post(`/projects/${projectId}/download-center/${dcJob.job_id}/retry-part?part=${partIndex}`);
      toast.info(`Rebuilding part ${partIndex}…`);
      const deadline = Date.now() + 20 * 60 * 1000;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 2500));
        let data;
        try {
          ({ data } = await api.get(`/download-center/${dcJob.job_id}`));
        } catch {
          continue;
        }
        setDcJob(data);
        const c = (data.chunks || []).find((x) => x.index === partIndex);
        if (c && (c.status === "ready" || c.status === "error")) {
          if (c.status === "ready") toast.success(`Part ${partIndex} rebuilt`);
          else toast.warning(`Part ${partIndex} failed again — try once more`);
          break;
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Retry failed");
    } finally {
      setRetrying((m) => {
        const n = { ...m };
        delete n[partIndex];
        return n;
      });
    }
  };

  // Poll the combine step (job.status stays "ready"; we watch combined.status).
  const pollCombine = async (jobId) => {
    const deadline = Date.now() + 30 * 60 * 1000;
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 2000));
      try {
        const { data } = await api.get(`/download-center/${jobId}`);
        setDcJob(data);
        const cs = data.combined?.status;
        if (cs === "ready" || cs === "error") return data;
      } catch {
        /* transient */
      }
    }
    return null;
  };

  // Merge all ready parts into ONE complete PDF — the reliable single-file download.
  const combineParts = async () => {
    if (!dcJob?.job_id) return;
    setCombineBusy(true);
    try {
      await api.post(`/projects/${projectId}/download-center/${dcJob.job_id}/combine?email=${emailWhenReady}`);
      toast.success("Combining parts into one complete PDF…");
      const res = await pollCombine(dcJob.job_id);
      if (res?.combined?.status === "ready") toast.success("Complete PDF ready — downloading");
      else if (res?.combined?.status === "error") toast.error(res.combined.error || "Could not combine");
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Could not combine parts");
    } finally {
      setCombineBusy(false);
    }
  };


  const onCoverPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/projects/${projectId}/cover`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Cover image added — it'll appear as the first page of your PDF");
      await loadCover();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not upload cover");
    } finally {
      setCoverBusy(false);
      if (coverInputRef.current) coverInputRef.current.value = "";
    }
  };

  const removeCover = async () => {
    setCover(null);
    try {
      await api.delete(`/projects/${projectId}/cover`);
      toast.success("Cover removed");
    } catch {
      /* best-effort */
    }
  };

  // Shared live progress bar (used by the hero and each background format export).
  const ProgressView = ({ prog, testid }) => {
    if (!prog || !prog.total) return null;
    const pct = Math.min(100, Math.round((prog.done / prog.total) * 100));
    const elapsed = (Date.now() - prog.startedAt) / 1000;
    const rate = prog.done > 0 ? prog.done / elapsed : 0;
    const eta = rate > 0 ? (prog.total - prog.done) / rate : null;
    return (
      <div className="mt-3" data-testid={`${testid}-progress`}>
        <div className="mb-1.5 flex items-center justify-between text-xs text-muted-foreground">
          <span data-testid={`${testid}-progress-label`}>
            Rendering document {prog.done.toLocaleString()} of {prog.total.toLocaleString()}
          </span>
          <span>
            {pct}%{eta && prog.done > 0 ? ` · ~${fmtSecs(eta)} left` : ""}
          </span>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-secondary">
          <div
            className="h-full rounded-full bg-primary transition-[width] duration-500 ease-out"
            style={{ width: `${Math.max(3, pct)}%` }}
            data-testid={`${testid}-progress-bar`}
          />
        </div>
      </div>
    );
  };

  const qs = (fmt, split) => `format=${fmt}${split ? "&split=true" : ""}`;

  // Poll a specific job until it finishes (used for retries and background exports).
  const pollJob = async (jobId) => {
    const deadline = Date.now() + 20 * 60 * 1000;
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 3000));
      try {
        const { data: st } = await api.get(`/export-jobs/${jobId}`);
        if (st.status === "ready") return { url: `${ORIGIN}${st.download_path}`, filename: st.filename, size: st.size };
        if (st.status === "error") throw new Error(st.error || "Export failed");
      } catch (e) {
        if (e.message && e.message !== "Network Error") throw e;
      }
    }
    throw new Error("Export is taking longer than expected — check Recent exports shortly");
  };

  // Start a background export job and poll until the file is ready in cloud storage.
  const prepareAsync = async (fmt, split = false) => {
    const { data } = await api.post(`/projects/${projectId}/export-async?${qs(fmt, split)}${emailParam()}`);
    return pollJob(data.job_id);
  };

  // Poll a job and surface live progress (done/total) so big books never feel stuck.
  const pollJobWithProgress = async (jobId, onProgress) => {
    const deadline = Date.now() + 60 * 60 * 1000; // 1h for very large books
    const startedAt = Date.now();
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 2000));
      try {
        const { data: st } = await api.get(`/export-jobs/${jobId}`);
        if (st.progress && st.progress.total) onProgress({ ...st.progress, startedAt });
        if (st.status === "ready") return { url: `${ORIGIN}${st.download_path}`, filename: st.filename, size: st.size };
        if (st.status === "error") throw new Error(st.error || "Export failed");
      } catch (e) {
        if (e.message && e.message !== "Network Error") throw e;
      }
    }
    throw new Error("Export is taking longer than expected — check Recent exports shortly");
  };

  // One-click hero: download the whole publication as PDF. Very large books build the
  // faster per-module Split ZIP automatically; big builds run in the background with a
  // live progress bar and auto-download when ready.
  const downloadPdfHero = async () => {
    const split = heroSplit;
    setHeroBusy(true);
    setHeroProg(needsAsync("pdf") ? { done: 0, total: docs.length, startedAt: Date.now() } : null);
    try {
      if (needsAsync("pdf")) {
        const { data } = await api.post(`/projects/${projectId}/export-async?${qs("pdf", split)}${emailParam()}`);
        const r = await pollJobWithProgress(data.job_id, setHeroProg);
        triggerDownload(r.url, r.filename || `${project.title}${split ? "_volumes.zip" : EXT.pdf}`);
        toast.success(split ? "Per-module PDFs ready — downloading" : "PDF ready — downloading");
        loadJobs();
      } else {
        await downloadFile(`/projects/${projectId}/export?${qs("pdf", split)}`, `${project.title}${split ? "_volumes.zip" : EXT.pdf}`);
        toast.success("PDF exported");
      }
    } catch (e) {
      toast.error(e.message || "Export failed");
    } finally {
      setHeroBusy(false);
      setHeroProg(null);
    }
  };

  const retryJob = async (jobId) => {
    try {
      const { data } = await api.post(`/export-jobs/${jobId}/retry`);
      toast.info("Retrying export…");
      loadJobs();
      await pollJob(data.job_id);
      toast.success("Export ready");
    } catch (e) {
      toast.error(e.message || "Retry failed");
    } finally {
      loadJobs();
    }
  };

  const dismissJob = async (jobId) => {
    setJobs((j) => j.filter((x) => x.job_id !== jobId));
    api.delete(`/export-jobs/${jobId}`).catch(() => {});
  };

  // ----- Export presets (per-user bundles of formats) -----
  const togglePresetFmt = (fmt) =>
    setPresetSel((s) => ({ ...s, [fmt]: !s[fmt] }));

  const savePreset = async () => {
    const chosen = FORMATS.filter((f) => presetSel[f.fmt]);
    if (!presetName.trim()) return toast.error("Give the preset a name");
    if (chosen.length === 0) return toast.error("Pick at least one format");
    setSavingPreset(true);
    try {
      const items = chosen.map((f) => ({ format: f.fmt, split: f.fmt === "pdf" ? presetSplit : false }));
      await api.post(`/export-presets`, { name: presetName.trim(), items });
      toast.success("Preset saved");
      setPresetOpen(false);
      setPresetName("");
      setPresetSel({});
      setPresetSplit(false);
      loadPresets();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save preset");
    } finally {
      setSavingPreset(false);
    }
  };

  const deletePreset = async (id) => {
    setPresets((p) => p.filter((x) => x.id !== id));
    setPresetLinks((l) => {
      const n = { ...l };
      delete n[id];
      return n;
    });
    api.delete(`/export-presets/${id}`).catch(() => {});
  };

  const runPreset = async (preset) => {
    setRunningPreset(preset.id);
    try {
      for (const it of preset.items) {
        await api.post(`/projects/${projectId}/export-async?${qs(it.format, it.split)}${emailParam()}`);
      }
      toast.success(`"${preset.name}" started — files will appear in Recent exports`);
      loadJobs();
      // light background poll so the history fills in as each file finishes
      const t = setInterval(loadJobs, 4000);
      setTimeout(() => clearInterval(t), 120000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not run preset");
    } finally {
      setRunningPreset(null);
    }
  };

  // Build every format in the preset and collect a copyable 7-day share link for each.
  const sharePreset = async (preset) => {
    setSharingPreset(preset.id);
    setPresetLinks((l) => ({ ...l, [preset.id]: [] }));
    try {
      const links = [];
      for (const it of preset.items) {
        try {
          const r = await prepareAsync(it.format, it.split);
          links.push({
            label: `${FORMAT_NAME[it.format] || it.format}${it.split ? " · Split" : ""}`,
            url: r.url,
          });
          setPresetLinks((l) => ({ ...l, [preset.id]: [...links] }));
        } catch (e) {
          links.push({ label: `${FORMAT_NAME[it.format] || it.format}`, url: null, error: true });
          setPresetLinks((l) => ({ ...l, [preset.id]: [...links] }));
        }
      }
      loadJobs();
      const ok = links.filter((x) => x.url).length;
      if (ok) toast.success(`${ok} shareable link${ok > 1 ? "s" : ""} ready (valid 7 days)`);
      else toast.error("Could not create share links");
    } finally {
      setSharingPreset(null);
    }
  };

  const triggerDownload = (url, filename) => {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "export";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const run = async (fmt, split = false) => {
    const key = split ? `${fmt}-split` : fmt;
    setBusy(key);
    setFormatProg((p) => ({ ...p, [key]: needsAsync(fmt) ? { done: 0, total: docs.length, startedAt: Date.now() } : null }));
    try {
      if (needsAsync(fmt)) {
        const { data } = await api.post(`/projects/${projectId}/export-async?${qs(fmt, split)}${emailParam()}`);
        const r = await pollJobWithProgress(data.job_id, (pr) => setFormatProg((p) => ({ ...p, [key]: pr })));
        triggerDownload(r.url, r.filename || `${project.title}${split ? "_volumes.zip" : EXT[fmt]}`);
        toast.success(`${FORMAT_NAME[fmt] || fmt.toUpperCase()} ready — downloading`);
        loadJobs();
      } else {
        await downloadFile(`/projects/${projectId}/export?${qs(fmt, split)}`, `${project.title}${split ? "_volumes.zip" : EXT[fmt]}`);
        toast.success(`${FORMAT_NAME[fmt] || fmt.toUpperCase()} exported`);
      }
    } catch (e) {
      toast.error(e.message || "Export failed");
    } finally {
      setBusy(null);
      setFormatProg((p) => {
        const n = { ...p };
        delete n[key];
        return n;
      });
    }
  };

  const share = async (fmt) => {
    setSharing(fmt);
    try {
      let link;
      if (needsAsync(fmt)) {
        const r = await prepareAsync(fmt);
        link = { url: r.url, size: r.size, async: true };
        loadJobs();
      } else {
        const { data } = await api.post(`/projects/${projectId}/share?${qs(fmt, false)}`);
        link = { url: `${ORIGIN}${data.path}`, expires_at: data.expires_at, size: data.size };
      }
      setLinks((l) => ({ ...l, [fmt]: link }));
      try {
        await navigator.clipboard.writeText(link.url);
        toast.success("Shareable link created & copied");
      } catch {
        toast.success("Shareable link created");
      }
    } catch (e) {
      toast.error(e.message || "Could not create link");
    } finally {
      setSharing(null);
    }
  };

  const copy = async (fmt, url) => {
    const ok = () => {
      setCopied(fmt);
      setTimeout(() => setCopied(null), 1500);
    };
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
        ok();
        return;
      }
      throw new Error("insecure");
    } catch {
      // Fallback for non-secure contexts / restricted browsers
      try {
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        const done = document.execCommand("copy");
        ta.remove();
        if (done) ok();
        else toast.error("Copy failed — select and copy the link manually");
      } catch {
        toast.error("Copy failed — select and copy the link manually");
      }
    }
  };

  if (docs.length === 0) {
    return (
      <div className="animate-fade-in rounded-sm border border-dashed border-border py-20 text-center text-muted-foreground">
        Nothing to export yet — generate some documents first.
      </div>
    );
  }

  return (
    <div className="animate-fade-in space-y-6">
      <div>
        <h2 className="font-serif text-2xl font-medium tracking-tight">Export &amp; publish</h2>
        <p className="mt-2 text-muted-foreground">
          {docs.length.toLocaleString()} documents ready.
        </p>
      </div>

      {/* One-click hero — download the whole publication as a PDF, no hunting */}
      <div
        className="rounded-sm border border-primary/30 bg-gradient-to-br from-secondary/60 to-card p-6 sm:p-8"
        data-testid="pdf-hero"
      >
        <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-4">
            <div className="mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-sm bg-primary/10 text-primary">
              <BookMarked size={22} />
            </div>
            <div>
              <h3 className="font-serif text-xl font-semibold tracking-tight">Download your publication</h3>
              <p className="mt-1 max-w-md text-sm text-muted-foreground">
                {heroSplit
                  ? `Large book — we'll build faster per-module PDFs bundled in a ZIP so it downloads quickly and reliably.`
                  : `A bound PDF with cover, table of contents and every document.`}
              </p>
            </div>
          </div>
          <Button
            size="lg"
            onClick={downloadPdfHero}
            disabled={heroBusy}
            className="h-12 shrink-0 rounded-sm px-8 text-base"
            data-testid="hero-download-pdf"
          >
            {heroBusy ? (
              <>
                <Loader2 size={18} className="mr-2 animate-spin" />
                {heroProg ? "Building your PDF…" : "Preparing…"}
              </>
            ) : (
              <>
                <Download size={18} className="mr-2" />
                {heroSplit ? "Download PDF (ZIP)" : "Download PDF"}
              </>
            )}
          </Button>
        </div>

        {heroBusy && heroProg && heroProg.total > 0 && (
          <ProgressView prog={heroProg} testid="hero" />
        )}
        {heroBusy && !heroProg && (
          <p className="mt-4 text-xs text-muted-foreground" data-testid="hero-progress-starting">
            Starting the build…
          </p>
        )}

        {/* Branded cover picker + Email-when-ready toggle */}
        <div className="mt-5 flex flex-col gap-3 border-t border-border/60 pt-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3" data-testid="cover-picker">
            {cover?.data_uri ? (
              <img
                src={cover.data_uri}
                alt="Cover"
                className="h-14 w-11 shrink-0 rounded-sm border border-border object-cover"
                data-testid="cover-thumb"
              />
            ) : (
              <div className="flex h-14 w-11 shrink-0 items-center justify-center rounded-sm border border-dashed border-border text-muted-foreground">
                <ImageIcon size={18} />
              </div>
            )}
            <div>
              <div className="text-sm font-medium">
                {cover?.data_uri ? "Cover image added" : "Branded cover (optional)"}
              </div>
              <div className="mt-0.5 flex items-center gap-3 text-xs">
                <button
                  type="button"
                  className="text-primary underline-offset-2 hover:underline disabled:opacity-50"
                  onClick={() => coverInputRef.current?.click()}
                  disabled={coverBusy}
                  data-testid="cover-upload-btn"
                >
                  {coverBusy ? "Uploading…" : cover?.data_uri ? "Replace" : "Add a cover image"}
                </button>
                {cover?.data_uri && (
                  <button
                    type="button"
                    className="text-muted-foreground underline-offset-2 hover:underline"
                    onClick={removeCover}
                    data-testid="cover-remove-btn"
                  >
                    Remove
                  </button>
                )}
                <span className="text-muted-foreground/70">PNG/JPG/WEBP · shown as page 1 of your PDF</span>
              </div>
            </div>
            <input
              ref={coverInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={onCoverPick}
              data-testid="cover-file-input"
            />
          </div>

          {anyAsync && (
            <label
              className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground"
              data-testid="email-when-ready-label"
            >
              <Checkbox
                checked={emailWhenReady}
                onCheckedChange={(v) => setEmailWhenReady(!!v)}
                data-testid="email-when-ready"
              />
              <Mail size={14} className="text-primary" />
              Email me the link when it's ready
            </label>
          )}
        </div>
      </div>

      {/* Download Center — split the book into fixed-size parts, each its own resumable link */}
      <div
        className={`rounded-sm border p-6 sm:p-7 ${bigBook ? "border-primary/30 bg-secondary/40" : "border-border"}`}
        data-testid="download-center"
      >
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-4">
            <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-sm bg-primary/10 text-primary">
              <Layers size={20} />
            </div>
            <div>
              <h3 className="font-serif text-lg font-semibold tracking-tight">Download in parts</h3>
              <p className="mt-1 max-w-lg text-sm text-muted-foreground">
                {bigBook ? "Recommended for large books — " : ""}we split your publication into smaller PDFs
                ({dcSize} documents each). Each part is its own link you can download (and resume) separately,
                so nothing chokes on one giant file.
              </p>
            </div>
          </div>
          <div className="flex shrink-0 flex-col items-stretch gap-2 sm:items-end">
            <div className="flex items-center gap-2">
              <select
                value={dcSize}
                onChange={(e) => setDcSize(Number(e.target.value))}
                disabled={dcBusy}
                className="h-9 rounded-sm border border-border bg-background px-2 text-sm"
                data-testid="dc-size-select"
              >
                <option value={25}>25 / part</option>
                <option value={50}>50 / part</option>
                <option value={100}>100 / part</option>
              </select>
              <Button onClick={prepareParts} disabled={dcBusy || docs.length === 0} className="rounded-sm" data-testid="dc-prepare-btn">
                {dcBusy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Layers size={16} className="mr-2" />}
                {dcJob ? "Rebuild parts" : "Prepare download links"}
              </Button>
            </div>
            <label
              className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground"
              data-testid="dc-auto-combine-label"
            >
              <Switch
                checked={dcAutoCombine}
                onCheckedChange={setDcAutoCombine}
                disabled={dcBusy}
                data-testid="dc-auto-combine-switch"
              />
              <BookMarked size={13} className="text-primary" />
              Auto-combine into one PDF when the last part is done
            </label>
            <label
              className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground"
              data-testid="dc-email-label"
            >
              <Checkbox
                checked={emailWhenReady}
                onCheckedChange={(v) => setEmailWhenReady(!!v)}
                disabled={dcBusy}
                data-testid="dc-email-when-ready"
              />
              <Mail size={13} className="text-primary" />
              Email me the complete-PDF link when it's ready
            </label>
          </div>
        </div>

        {dcJob && (
          <div className="mt-5" data-testid="dc-parts">
            <div className="mb-2 flex items-center justify-between text-xs text-muted-foreground">
              <span data-testid="dc-progress-label">
                {dcJob.status === "ready"
                  ? `${(dcJob.chunks || []).filter((c) => c.status === "ready").length} of ${dcJob.total_parts || (dcJob.chunks || []).length} parts ready`
                  : `Building… ${dcJob.progress?.done || 0} of ${dcJob.progress?.total || dcJob.total_parts || "?"} parts`}
              </span>
              {dcJob.status !== "ready" && dcBusy && <Loader2 size={13} className="animate-spin" />}
            </div>
            <div className="divide-y divide-border rounded-sm border border-border">
              {(dcJob.chunks || []).map((c) => (
                <div key={c.index} className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`dc-part-${c.index}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium">Part {c.index}</div>
                    <div className="text-xs text-muted-foreground">
                      Docs {c.from}–{c.to} · {c.count} document{c.count === 1 ? "" : "s"}
                      {c.size ? ` · ${c.size >= 1048576 ? `${(c.size / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(c.size / 1024))} KB`}` : ""}
                    </div>
                  </div>
                  {c.status === "ready" && c.download_path ? (
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-sm"
                      onClick={() => triggerDownload(`${ORIGIN}${c.download_path}`, c.filename)}
                      data-testid={`dc-download-${c.index}`}
                    >
                      <Download size={14} className="mr-1.5" /> Download
                    </Button>
                  ) : c.status === "error" ? (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-destructive">Failed</span>
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-sm"
                        disabled={!!retrying[c.index]}
                        onClick={() => retryParts(c.index)}
                        data-testid={`dc-retry-${c.index}`}
                      >
                        {retrying[c.index] ? (
                          <Loader2 size={13} className="mr-1.5 animate-spin" />
                        ) : (
                          <RotateCw size={13} className="mr-1.5" />
                        )}
                        {retrying[c.index] ? "Rebuilding" : "Retry"}
                      </Button>
                    </div>
                  ) : (
                    <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Loader2 size={13} className="animate-spin" /> Building
                    </span>
                  )}
                </div>
              ))}
            </div>

            {/* Retry-all for failed parts (keeps ready parts untouched) */}
            {(dcJob.chunks || []).some((c) => c.status === "error") && !dcBusy && (
              <div className="mt-3 flex items-center justify-between rounded-sm border border-destructive/30 bg-destructive/5 px-4 py-3">
                <span className="text-sm text-muted-foreground">
                  {(dcJob.chunks || []).filter((c) => c.status === "error").length} part(s) failed. Retry just those — your ready parts stay as they are.
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-sm"
                  onClick={() => retryParts(null)}
                  data-testid="dc-retry-all-failed"
                >
                  <RotateCw size={13} className="mr-1.5" /> Retry failed parts
                </Button>
              </div>
            )}

            {/* Combine every ready part into ONE complete PDF — reliable single-file download */}
            {dcJob.all_parts_ready && (
              <div className="mt-4 rounded-sm border border-primary/30 bg-secondary/40 p-4" data-testid="dc-combine">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-start gap-3">
                    <BookMarked size={18} className="mt-0.5 shrink-0 text-primary" />
                    <div>
                      <div className="text-sm font-medium">Want it all in one file?</div>
                      <p className="mt-0.5 max-w-md text-xs text-muted-foreground">
                        Merge every part into a single complete PDF. This just stitches the finished parts together,
                        so it's fast and reliable even for very long works.
                      </p>
                    </div>
                  </div>
                  {dcJob.combined?.status === "ready" && dcJob.combined?.download_path ? (
                    <Button
                      className="shrink-0 rounded-sm"
                      onClick={() => triggerDownload(`${ORIGIN}${dcJob.combined.download_path}`, dcJob.combined.filename)}
                      data-testid="dc-combine-download"
                    >
                      <Download size={15} className="mr-1.5" />
                      Download complete PDF{dcJob.combined.size ? ` · ${fmtBytes(dcJob.combined.size)}` : ""}
                    </Button>
                  ) : (
                    <Button
                      className="shrink-0 rounded-sm"
                      disabled={combineBusy || dcJob.combined?.status === "building"}
                      onClick={combineParts}
                      data-testid="dc-combine-btn"
                    >
                      {combineBusy || dcJob.combined?.status === "building" ? (
                        <Loader2 size={15} className="mr-1.5 animate-spin" />
                      ) : (
                        <BookMarked size={15} className="mr-1.5" />
                      )}
                      {combineBusy || dcJob.combined?.status === "building" ? "Combining…" : "Combine into one PDF"}
                    </Button>
                  )}
                </div>
                {/* Live "merging part X of N" progress + ETA while stitching the complete PDF */}
                {dcJob.combined?.status === "building" && dcJob.combined?.progress?.total > 0 && (
                  <div className="mt-3" data-testid="dc-combine-progress">
                    <div className="mb-1.5 flex items-center justify-between text-xs text-muted-foreground">
                      <span data-testid="dc-combine-progress-label">
                        Merging part {dcJob.combined.progress.done} of {dcJob.combined.progress.total}
                      </span>
                      <span data-testid="dc-combine-eta">
                        {Math.min(100, Math.round((dcJob.combined.progress.done / dcJob.combined.progress.total) * 100))}%
                        {(() => {
                          const p = dcJob.combined.progress;
                          if (!p.started_at || p.done <= 0) return "";
                          const elapsed = (Date.now() - new Date(p.started_at).getTime()) / 1000;
                          const rate = p.done / elapsed;
                          if (!isFinite(rate) || rate <= 0) return "";
                          const eta = (p.total - p.done) / rate;
                          return eta > 1 ? ` · ~${fmtSecs(eta)} left` : "";
                        })()}
                      </span>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-secondary">
                      <div
                        className="h-full rounded-full bg-primary transition-[width] duration-500 ease-out"
                        style={{ width: `${Math.max(3, Math.round((dcJob.combined.progress.done / dcJob.combined.progress.total) * 100))}%` }}
                        data-testid="dc-combine-progress-bar"
                      />
                    </div>
                  </div>
                )}
                {dcJob.combined?.status === "building" && !dcJob.combined?.progress?.total && (
                  <p className="mt-2 text-xs text-muted-foreground" data-testid="dc-combine-starting">
                    Starting the merge…
                  </p>
                )}
                {dcJob.combined?.status === "error" && (
                  <p className="mt-2 text-xs text-destructive" data-testid="dc-combine-error">
                    {dcJob.combined.error || "Could not combine the parts — try again."}
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>


      <div>
        <Button
          variant="ghost"
          size="sm"
          className="rounded-sm text-muted-foreground"
          onClick={() => setShowMore((v) => !v)}
          data-testid="toggle-more-formats"
        >
          {showMore ? <ChevronUp size={15} className="mr-1.5" /> : <ChevronDown size={15} className="mr-1.5" />}
          {showMore ? "Hide other formats & options" : "More formats & options (EPUB, Word, ZIP, presets…)"}
        </Button>
      </div>

      {showMore && (
      <>
      {/* Export presets — reusable one-click bundles */}
      <div className="rounded-sm border border-border bg-card" data-testid="export-presets">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <div className="flex items-center gap-2">
            <Bookmark size={16} className="text-primary" />
            <h3 className="font-serif text-base font-semibold">Export presets</h3>
          </div>
          <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setPresetOpen(true)} data-testid="new-preset-btn">
            <Plus size={14} className="mr-1" /> New preset
          </Button>
        </div>
        {presets.length === 0 ? (
          <p className="px-5 py-4 text-sm text-muted-foreground">
            Save a favourite bundle (e.g. Split PDFs + EPUB) and fire it in one click.
          </p>
        ) : (
          <div className="divide-y divide-border/60">
            {presets.map((p) => (
              <div key={p.id} className="px-5 py-3" data-testid={`preset-${p.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium">{p.name}</div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {p.items.map((it, i) => (
                        <span key={i} className="rounded-sm bg-secondary px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                          {(FORMAT_NAME[it.format] || it.format)}{it.split ? " · split" : ""}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Button
                      size="sm"
                      className="rounded-sm"
                      onClick={() => runPreset(p)}
                      disabled={runningPreset === p.id || sharingPreset === p.id}
                      data-testid={`run-preset-${p.id}`}
                    >
                      {runningPreset === p.id ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Play size={14} className="mr-1" />}
                      Run
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-sm"
                      onClick={() => sharePreset(p)}
                      disabled={sharingPreset === p.id || runningPreset === p.id}
                      data-testid={`share-preset-${p.id}`}
                    >
                      {sharingPreset === p.id ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Link2 size={14} className="mr-1" />}
                      Share links
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8 text-muted-foreground"
                      onClick={() => setDeleteTarget(p)}
                      data-testid={`delete-preset-${p.id}`}
                      title="Delete preset"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
                {presetLinks[p.id] && presetLinks[p.id].length > 0 && (
                  <div className="mt-3 space-y-1.5 rounded-sm border border-border bg-secondary/30 p-2" data-testid={`preset-links-${p.id}`}>
                    {presetLinks[p.id].map((lnk, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className="w-24 shrink-0 truncate text-[10px] uppercase tracking-wider text-muted-foreground">{lnk.label}</span>
                        {lnk.url ? (
                          <>
                            <input
                              readOnly
                              value={lnk.url}
                              onFocus={(e) => e.target.select()}
                              className="min-w-0 flex-1 truncate bg-transparent text-xs text-muted-foreground outline-none"
                              data-testid={`preset-link-url-${p.id}-${i}`}
                            />
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-6 w-6 shrink-0"
                              onClick={() => copy(`preset-${p.id}-${i}`, lnk.url)}
                              data-testid={`preset-link-copy-${p.id}-${i}`}
                            >
                              {copied === `preset-${p.id}-${i}` ? <Check size={13} className="text-primary" /> : <Copy size={13} />}
                            </Button>
                          </>
                        ) : sharingPreset === p.id ? (
                          <span className="flex items-center gap-1 text-xs text-muted-foreground"><Loader2 size={11} className="animate-spin" /> building…</span>
                        ) : (
                          <span className="text-xs text-destructive">failed</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {FORMATS.map((f) => {
          const link = links[f.fmt];
          return (
            <div key={f.fmt} className="flex flex-col rounded-sm border border-border bg-card p-6">
              <f.icon size={22} className="text-primary" />
              <h3 className="mt-3 font-serif text-lg font-semibold">{f.name}</h3>
              <p className="mt-1 flex-1 text-sm text-muted-foreground">{f.desc}</p>
              <div className="mt-4 flex flex-col gap-2">
                <Button
                  onClick={() => run(f.fmt)}
                  disabled={busy === f.fmt}
                  className="rounded-sm"
                  variant="outline"
                  data-testid={`export-${f.fmt}`}
                >
                  {busy === f.fmt ? <Loader2 size={15} className="mr-2 animate-spin" /> : null}
                  {busy === f.fmt && needsAsync(f.fmt) ? "Preparing…" : `Export ${f.name}`}
                </Button>
                <Button
                  onClick={() => share(f.fmt)}
                  disabled={sharing === f.fmt}
                  className="rounded-sm"
                  variant="ghost"
                  size="sm"
                  data-testid={`share-${f.fmt}`}
                >
                  {sharing === f.fmt ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Link2 size={14} className="mr-2" />}
                  Get shareable link
                </Button>
                {f.fmt === "pdf" && (
                  <Button
                    onClick={() => run("pdf", true)}
                    disabled={busy === "pdf-split"}
                    className="rounded-sm"
                    variant={bigBook ? "default" : "ghost"}
                    size="sm"
                    data-testid="export-pdf-split"
                  >
                    {busy === "pdf-split" ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Layers size={14} className="mr-2" />}
                    {busy === "pdf-split" ? "Preparing volumes…" : "Split into per-module PDFs (ZIP)"}
                  </Button>
                )}
                {f.fmt === "pdf" && bigBook && (
                  <p className="text-xs leading-snug text-amber-700" data-testid="split-nudge">
                    This book is large ({docs.length.toLocaleString()} docs) — per-module Split PDFs build faster and more reliably than one giant file.
                  </p>
                )}
                {busy === f.fmt && formatProg[f.fmt] && <ProgressView prog={formatProg[f.fmt]} testid={`fmt-${f.fmt}`} />}
                {busy === `${f.fmt}-split` && formatProg[`${f.fmt}-split`] && (
                  <ProgressView prog={formatProg[`${f.fmt}-split`]} testid={`fmt-${f.fmt}-split`} />
                )}
              </div>
              {link && (
                <div className="mt-3 rounded-sm border border-border bg-secondary/40 p-2" data-testid={`share-link-${f.fmt}`}>
                  <div className="flex items-center gap-1">
                    <input
                      readOnly
                      value={link.url}
                      className="min-w-0 flex-1 truncate bg-transparent text-xs text-muted-foreground outline-none"
                      onFocus={(e) => e.target.select()}
                      data-testid={`share-url-${f.fmt}`}
                    />
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-6 w-6 shrink-0"
                      onClick={() => copy(f.fmt, link.url)}
                      data-testid={`copy-link-${f.fmt}`}
                    >
                      {copied === f.fmt ? <Check size={13} className="text-primary" /> : <Copy size={13} />}
                    </Button>
                  </div>
                  <div className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                    {fmtBytes(link.size)}
                    {link.expires_at ? ` · expires ${new Date(link.expires_at).toLocaleDateString()}` : " · valid 7 days"}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      </>
      )}

      {jobs.length > 0 && (
        <div className="rounded-sm border border-border bg-card" data-testid="export-history">
          <div className="flex items-center justify-between border-b border-border px-5 py-3">
            <div className="flex items-center gap-2">
              <History size={16} className="text-primary" />
              <h3 className="font-serif text-base font-semibold">Recent exports</h3>
            </div>
            <Button size="sm" variant="ghost" className="rounded-sm" onClick={loadJobs} data-testid="refresh-export-history">
              Refresh
            </Button>
          </div>
          <div className="divide-y divide-border/60">
            {jobs.map((j) => (
              <div key={j.job_id} className="flex items-center justify-between gap-3 px-5 py-3 text-sm" data-testid={`export-job-${j.job_id}`}>
                <div className="min-w-0">
                  <span className="rounded-sm bg-secondary px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                    {j.format}{j.split ? " · split" : ""}
                  </span>{" "}
                  <span className="text-muted-foreground">{j.filename || "building…"}</span>
                  {j.size ? <span className="ml-2 text-xs text-muted-foreground/70">{fmtBytes(j.size)}</span> : null}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {j.status === "ready" && j.download_path ? (
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-sm"
                      onClick={() => triggerDownload(`${ORIGIN}${j.download_path}`, j.filename)}
                      data-testid={`redownload-${j.job_id}`}
                    >
                      <Download size={14} className="mr-1" /> Download
                    </Button>
                  ) : j.status === "error" ? (
                    <>
                      <span className="text-xs text-destructive">Failed</span>
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-sm"
                        onClick={() => retryJob(j.job_id)}
                        data-testid={`retry-${j.job_id}`}
                      >
                        <RotateCw size={13} className="mr-1" /> Retry
                      </Button>
                    </>
                  ) : j.status === "expired" ? (
                    <span className="text-xs text-muted-foreground">Expired</span>
                  ) : (
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Loader2 size={12} className="animate-spin" /> {j.status}
                    </span>
                  )}
                  {["ready", "error", "expired"].includes(j.status) && (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 shrink-0 text-muted-foreground"
                      onClick={() => dismissJob(j.job_id)}
                      data-testid={`dismiss-${j.job_id}`}
                      title="Remove from history"
                    >
                      <X size={14} />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={presetOpen} onOpenChange={setPresetOpen}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="preset-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">New export preset</DialogTitle>
            <DialogDescription>Bundle the formats you export together so you can run them in one click.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-xs uppercase tracking-wider text-muted-foreground">Preset name</label>
              <Input
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                placeholder="e.g. Publish bundle"
                className="rounded-sm"
                data-testid="preset-name-input"
              />
            </div>
            <div>
              <label className="mb-2 block text-xs uppercase tracking-wider text-muted-foreground">Formats</label>
              <div className="grid grid-cols-2 gap-2">
                {FORMATS.map((f) => (
                  <label
                    key={f.fmt}
                    className="flex cursor-pointer items-center gap-2 rounded-sm border border-border px-3 py-2 text-sm"
                    data-testid={`preset-fmt-${f.fmt}`}
                  >
                    <Checkbox checked={!!presetSel[f.fmt]} onCheckedChange={() => togglePresetFmt(f.fmt)} />
                    <span className="truncate">{f.name}</span>
                  </label>
                ))}
              </div>
            </div>
            {presetSel.pdf && (
              <div className="flex items-center justify-between rounded-sm border border-border px-3 py-2">
                <span className="text-sm text-muted-foreground">Split the PDF into per-module volumes (ZIP)</span>
                <Switch checked={presetSplit} onCheckedChange={setPresetSplit} data-testid="preset-split-switch" />
              </div>
            )}
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" className="rounded-sm" onClick={() => setPresetOpen(false)} data-testid="preset-cancel">
              Cancel
            </Button>
            <Button className="rounded-sm" onClick={savePreset} disabled={savingPreset} data-testid="preset-save">
              {savingPreset ? <Loader2 size={15} className="mr-2 animate-spin" /> : <Bookmark size={15} className="mr-2" />}
              Save preset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-sm" data-testid="delete-preset-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif">Delete this preset?</AlertDialogTitle>
            <AlertDialogDescription>
              “{deleteTarget?.name}” will be removed. This can't be undone, but you can always create it again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm" data-testid="delete-preset-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="rounded-sm bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (deleteTarget) deletePreset(deleteTarget.id);
                setDeleteTarget(null);
              }}
              data-testid="delete-preset-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
